/**
 * Copyright (c) 2025 Aisle 24. All rights reserved.
 *
 * Proprietary Software - Licensed for Internal Use Only
 *
 * Update Notification Component
 * Shows notification when new version is available
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  SystemUpdateAlt,
  Close,
  ExpandMore,
  ExpandLess,
  Info,
} from '@mui/icons-material';
import { UpdateCheckResult } from '../services/UpdateChecker';
import { updateChecker } from '../services/UpdateChecker';

interface UpdateNotificationProps {
  updateInfo: UpdateCheckResult;
  onDismiss?: () => void;
  onUpdate?: () => void;
}

export const UpdateNotification: React.FC<UpdateNotificationProps> = ({
  updateInfo,
  onDismiss,
  onUpdate,
}) => {
  const [open, setOpen] = useState(true);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleUpdate = () => {
    setDialogOpen(true);
  };

  const handleConfirmUpdate = () => {
    setDialogOpen(false);
    setOpen(false);
    updateChecker.triggerUpdate();
    onUpdate?.();
  };

  const handleDismiss = () => {
    setOpen(false);
    onDismiss?.();
  };

  if (!open) return null;

  return (
    <>
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 9999,
          maxWidth: 400,
          backgroundColor: '#2C507A',
          color: 'white',
          borderRadius: 2,
          overflow: 'visible',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <SystemUpdateAlt sx={{ mr: 1, fontSize: 28 }} />
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 'bold' }}>
              Update Available
            </Typography>
            <IconButton
              size="small"
              onClick={handleDismiss}
              sx={{ color: 'white' }}
            >
              <Close />
            </IconButton>
          </Box>

          <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
            Version {updateInfo.latestVersion} (Build {updateInfo.latestBuild}) is available.
            <br />
            Current: {updateInfo.currentVersion} (Build {updateInfo.currentBuild})
          </Typography>

          {updateInfo.forceUpdate && (
            <Chip
              label="Required Update"
              color="error"
              size="small"
              sx={{ mb: 1, backgroundColor: '#F06C38', color: 'white' }}
            />
          )}

          {/* Release Notes - Always visible if available */}
          {updateInfo.releaseNotes && updateInfo.releaseNotes !== 'No release notes available' && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: 1,
                maxHeight: 150,
                overflow: 'auto',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: 'bold',
                  mb: 0.5,
                  opacity: 0.9,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Release Notes:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-line',
                  fontSize: '0.8rem',
                  lineHeight: 1.6,
                  opacity: 0.95,
                }}
              >
                {updateInfo.releaseNotes}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleUpdate}
              startIcon={<SystemUpdateAlt />}
              sx={{
                flex: 1,
                backgroundColor: '#F06C38',
                '&:hover': { backgroundColor: '#d45a2a' },
              }}
            >
              Update Now
            </Button>
            {updateInfo.releaseNotes && updateInfo.releaseNotes !== 'No release notes available' && (
              <Button
                variant="outlined"
                onClick={() => setShowReleaseNotes(!showReleaseNotes)}
                startIcon={showReleaseNotes ? <ExpandLess /> : <ExpandMore />}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                }}
              >
                {showReleaseNotes ? 'Hide Details' : 'More Details'}
              </Button>
            )}
          </Box>

          <Collapse in={showReleaseNotes}>
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                backgroundColor: 'rgba(0,0,0,0.4)',
                borderRadius: 1,
                maxHeight: 300,
                overflow: 'auto',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-line',
                  fontSize: '0.85rem',
                  lineHeight: 1.8,
                }}
              >
                {updateInfo.releaseNotes || 'No release notes available'}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Info sx={{ mr: 1, color: '#2C507A' }} />
            Confirm Update
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The application will restart to apply the update. Any unsaved work will be lost.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>New Version:</strong> {updateInfo.latestVersion} (Build {updateInfo.latestBuild})
            <br />
            <strong>Current Version:</strong> {updateInfo.currentVersion} (Build {updateInfo.currentBuild})
          </Typography>
          {updateInfo.releaseNotes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Release Notes:
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'pre-line', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}
              >
                {updateInfo.releaseNotes}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmUpdate}
            variant="contained"
            sx={{ backgroundColor: '#2C507A' }}
          >
            Update & Restart
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

